import {
  isPlaybookSectionNode,
  type FlattenedPlaybookNode,
  type PlaybookNodeId,
} from "@repo/app-types/entities/playbook";
import { popoverStyle } from "@repo/ui/popover/style";
import {
  IconCheck,
  IconChevronDown,
  IconCornerDownRight,
} from "@tabler/icons-react";
import type { FC } from "react";
import {
  Button as AriaButton,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

/** Left padding of a top-level row, in px, and the step added per nesting level. */
const rowBasePadding = 12;
const rowIndentStep = 20;

export interface FeedbackReviewJumpToPopoverProps {
  flattenedNodes: readonly FlattenedPlaybookNode[];
  openFeedbackCounts: ReadonlyMap<PlaybookNodeId, number>;
  selectedNodeId: PlaybookNodeId | null;
  onSelectNode: (nodeId: PlaybookNodeId) => void;
  isDisabled?: boolean;
}

/**
 * The "Jump to" half of the criterion navigation: the whole playbook tree in
 * reading order, with the amount of open feedback on each node, so a reviewer
 * can pick where to go next instead of stepping there with the arrows.
 *
 * A react-aria `Select` + `ListBox` rather than a hand-rolled list (mirroring
 * `SimpleTreeSelect`): that carries the listbox semantics, arrow-key
 * navigation, typeahead over node names, close-on-select, and opening focused
 * and scrolled to the selected node.
 */
export const FeedbackReviewJumpToPopover: FC<
  FeedbackReviewJumpToPopoverProps
> = (props) => {
  const {
    flattenedNodes,
    openFeedbackCounts,
    selectedNodeId,
    onSelectNode,
    isDisabled,
  } = props;

  return (
    <Select
      className="contents"
      aria-label="Jump to criteria"
      isDisabled={isDisabled}
      value={selectedNodeId}
      onChange={(key) => {
        if (key !== null) {
          onSelectNode(key as PlaybookNodeId);
        }
      }}
    >
      <AriaButton className="inline-flex h-7 cursor-pointer items-center gap-1 border-x border-border-strong bg-white px-2.5 text-sm text-foreground hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary disabled:cursor-default disabled:bg-disabled disabled:text-muted-foreground">
        Jump to
        <IconChevronDown className="size-4 text-gray-400" />
      </AriaButton>

      <Popover placement="bottom end" className={popoverStyle().popover()}>
        <ListBox className="max-h-[50vh] w-[28rem] max-w-[calc(100vw-2rem)] overflow-y-auto py-1 outline-hidden">
          {flattenedNodes.map(({ node, depth }) => {
            const isSection = isPlaybookSectionNode(node);
            const openCount = openFeedbackCounts.get(node.id) ?? 0;

            return (
              <ListBoxItem
                key={node.id}
                id={node.id}
                textValue={node.name}
                className={twMerge(
                  "flex w-full cursor-pointer items-center gap-2 py-1.5 pr-3 text-left text-sm text-foreground outline-hidden hover:bg-gray-50 focus:bg-gray-50 selected:bg-indigo-50/50",
                  isSection && "font-medium",
                )}
                style={{ paddingLeft: rowBasePadding + depth * rowIndentStep }}
              >
                {({ isSelected }) => (
                  <>
                    {isSection ? null : (
                      <IconCornerDownRight className="size-4 shrink-0 text-gray-400" />
                    )}
                    <span className="min-w-0 flex-1 truncate" title={node.name}>
                      {node.name}
                    </span>
                    {openCount > 0 ? (
                      <span className="inline-flex shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-600">
                        {openCount}
                      </span>
                    ) : null}
                    {isSelected ? (
                      <IconCheck className="size-4 shrink-0 text-indigo-600" />
                    ) : null}
                  </>
                )}
              </ListBoxItem>
            );
          })}
        </ListBox>
      </Popover>
    </Select>
  );
};
